import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import type { InventoryVariant, ModelGroup, ProductMedia, ProviderSlot } from '@/lib/api/schemas/inventory.schema'
import { getProductMedia, getVariantModelGroup } from '@/lib/api/client/inventory'
import { pieceById, PieceReference } from '@/components/piece/PieceReference'
import { ProductMediaGallery } from '@/components/warehouse/ProductMediaGallery'
import { LogsDisclosure, PieceCharacteristics, TechnicalIdentityLog } from '@/components/warehouse/PieceCharacteristics'
import { ProductCardIdentity } from '@/components/warehouse/ProductCardIdentity'
import { ProductMovementDetails } from '@/components/warehouse/ProductMovementDetails'
import { ProductEconomics } from '@/components/warehouse/ProductEconomics'
import { ProductOrderHistory } from '@/components/warehouse/ProductOrderHistory'
import { ProductPrices } from '@/components/warehouse/ProductPrices'
import { ProductProblems } from '@/components/warehouse/ProductProblems'
import { PublishedDescription } from '@/components/warehouse/PublishedDescription'
import { SectionCard } from '@/components/warehouse/primitives'
import { VariationFeed } from '@/components/warehouse/VariationFeed'
import {
  discrepancyLabels,
  labelFor,
  matchBucketLabels,
  matchReadUnavailableReasonLabels,
  matchReasonLabels,
  stockMismatchReasonLabels,
  warehouseDateTime,
} from '@/lib/warehouse/labels'
import {
  displayArticle,
  marketplaceBadges,
  stockDisagreementsOf,
  stockMismatchFindingsOf,
} from '@/lib/warehouse/projection'

interface Props {
  variant: InventoryVariant
  providerSlots: readonly ProviderSlot[] | null
  onClose: () => void
  onVariantPick: (variantId: string) => void
  marketSection?: ReactNode
}

type AsyncSectionState<T> = {
  key: string
  value: T | null
  error: string | null
  loading: boolean
}

function loadingSection<T>(key: string): AsyncSectionState<T> {
  return { key, value: null, error: null, loading: true }
}

function sectionError(reason: unknown): string {
  return reason instanceof Error ? reason.message : 'Данные карточки недоступны.'
}

export function ProductQuickPreview({ variant, providerSlots, onClose, onVariantPick, marketSection }: Props) {
  const { identity } = variant
  const selectionKey = `${variant.productId}:${identity.canonicalProductId}`
  const [mediaState, setMediaState] = useState<AsyncSectionState<ProductMedia>>(() => loadingSection<ProductMedia>(selectionKey))
  const [modelGroupState, setModelGroupState] = useState<AsyncSectionState<ModelGroup>>(() => loadingSection<ModelGroup>(selectionKey))
  const badges = marketplaceBadges(variant, providerSlots)
  const disagreements = stockDisagreementsOf(variant)
  const mismatchFindings = stockMismatchFindingsOf(variant)

  // Media is read by product, the grain `product_media` is keyed by, so a
  // product whose catalogue holds no legacy variant still shows its own card.
  // The model feed stays variant-addressed: a model group is a variant fact.
  useEffect(() => {
    const controller = new AbortController()
    setMediaState(loadingSection<ProductMedia>(selectionKey))
    setModelGroupState(loadingSection<ModelGroup>(selectionKey))
    void getProductMedia(variant.productId, controller.signal).then((nextMedia) => {
      if (!controller.signal.aborted) setMediaState({ key: selectionKey, value: nextMedia, error: null, loading: false })
    }).catch((reason: unknown) => {
      if (!controller.signal.aborted) setMediaState({ key: selectionKey, value: null, error: sectionError(reason), loading: false })
    })
    void getVariantModelGroup(identity.canonicalProductId, controller.signal).then((nextGroup) => {
      if (!controller.signal.aborted) setModelGroupState({ key: selectionKey, value: nextGroup, error: null, loading: false })
    }).catch((reason: unknown) => {
      if (!controller.signal.aborted) setModelGroupState({ key: selectionKey, value: null, error: sectionError(reason), loading: false })
    })
    return () => controller.abort()
  }, [identity.canonicalProductId, selectionKey, variant.productId])

  // An effect runs after the first render for a new selection. Keep the key on
  // each result so that frame cannot show the previous piece's data under the
  // new header while the effect is resetting both reads.
  const currentMedia = mediaState.key === selectionKey ? mediaState : loadingSection<ProductMedia>(selectionKey)
  const currentModelGroup = modelGroupState.key === selectionKey ? modelGroupState : loadingSection<ModelGroup>(selectionKey)

  return (
    <div
      data-testid="warehouse-quick-preview"
      className="flex h-full min-h-0 w-full flex-col overflow-hidden border-l border-rule bg-paper"
    >
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-rule px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:flex-nowrap">
        <ProductCardIdentity variant={variant} badges={badges} />
        <div className="flex shrink-0 items-center gap-1">
          <PieceReference piece={pieceById(identity.canonicalProductId, variant.title)} originLabel="складу" className="rounded-md border border-rule px-2.5 py-1.5 text-[11px] font-medium text-ink-2 hover:bg-bone/60 hover:text-ink">
            Открыть полностью
          </PieceReference>
          <button type="button" onClick={onClose} aria-label="Закрыть быстрый просмотр" className="size-8 rounded-md text-lg text-ink-muted hover:bg-bone/60 hover:text-ink">
            ×
          </button>
        </div>
      </header>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-3">
        {currentModelGroup.value ? <VariationFeed model={currentModelGroup.value} onPick={onVariantPick} /> : currentModelGroup.error ? <p role="alert" className="text-xs text-st-unpaid">{currentModelGroup.error}</p> : currentModelGroup.loading ? <p aria-live="polite" className="text-xs text-ink-muted">Загружаем артикулы модели…</p> : null}
        {currentMedia.value ? <ProductMediaGallery
          media={currentMedia.value}
          heading={`${displayArticle(variant)} · ${variant.title ?? 'Без названия'}`}
          onRefresh={(nextMedia) => setMediaState((current) => current.key === selectionKey
            ? { key: selectionKey, value: nextMedia, error: null, loading: false }
            : current)}
        /> : currentMedia.error ? <p role="alert" className="text-xs text-st-unpaid">{currentMedia.error}</p> : currentMedia.loading ? <p aria-live="polite" className="text-xs text-ink-muted">Загружаем фото и видео…</p> : null}
        <ProductProblems
          problems={variant.problems ?? []}
          available={variant.problemsAvailable ?? false}
          unavailableReason={variant.problemsUnavailableReason ?? null}
        />
        <ProductPrices variant={variant} />
        <ProductEconomics variant={variant} />
        {marketSection}
        <PieceCharacteristics variant={variant} />
        <PublishedDescription variant={variant} />
        <ProductMovementDetails variant={variant} />
        <ProductOrderHistory productId={variant.productId} />

        <LogsDisclosure>
          <SectionCard title="Расхождение остатков">
            {disagreements.length === 0 && mismatchFindings.length === 0 ? (
              <p className="text-xs text-ink-muted">
                {variant.stockDisagreements
                  ? 'Расхождений с наблюдениями каналов нет.'
                  : 'Сервер ещё не публикует построчные расхождения остатков.'}
              </p>
            ) : disagreements.length > 0 ? (
              <ul className="space-y-1.5 text-xs">
                {disagreements.map((row) => (
                  <li key={`${row.accountCode}:${row.observedAt ?? 'none'}`} className="rounded-md border border-st-unpaid/30 bg-st-unpaid/5 px-2.5 py-2">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="font-medium text-ink">{row.accountCode}</span>
                      <time className="text-[10px] text-ink-muted">{warehouseDateTime(row.observedAt)}</time>
                    </div>
                    <p className="mt-1 text-ink-2">
                      Локально <strong>{row.canonicalQuantity}</strong> · канал <strong>{row.observedQuantity}</strong> · {labelFor(discrepancyLabels, row.reason)}
                    </p>
                  </li>
                ))}
              </ul>
            ) : null}
            {mismatchFindings.length > 0 ? <div data-testid="warehouse-stock-mismatch-evidence" className="mt-3 border-t border-rule pt-2">
              <p className="text-[11px] font-medium text-ink">Источники сигнала</p>
              <ul className="mt-1.5 space-y-1.5 text-xs">
                {mismatchFindings.map((finding) => (
                  <li key={`${finding.marketplaceAccountCode}:${finding.marketplaceObservedAt ?? 'none'}:${finding.reason}`} className="rounded-md border border-rule bg-bone/30 px-2.5 py-2" data-reason={finding.reason} data-authority={finding.ourAuthority ?? undefined}>
                    <p className="text-ink-2">
                      Наша сторона: <strong>{finding.ourQuantity ?? '—'}</strong> · {finding.ourAuthority === 'onec_exchange' ? 'обмен 1С' : 'полномочие не указано'} · аккаунт {finding.ourAccountCode ?? 'не указан'} · зафиксировано {finding.ourAsOf ? <time dateTime={finding.ourAsOf}>{warehouseDateTime(finding.ourAsOf)}</time> : 'нет наблюдения'}
                    </p>
                    <p className="mt-1 text-ink-2">
                      Площадка: <strong>{finding.marketplaceQuantity ?? '—'}</strong> · аккаунт {finding.marketplaceAccountCode} · наблюдение {finding.marketplaceObservedAt ? <time dateTime={finding.marketplaceObservedAt}>{warehouseDateTime(finding.marketplaceObservedAt)}</time> : 'не зафиксировано'} · {stockMismatchReasonLabels[finding.reason]}
                    </p>
                  </li>
                ))}
              </ul>
            </div> : null}
          </SectionCard>

          <SectionCard title="Состояние сопоставления">
            <p className="text-xs text-ink-2">
              {variant.matchBucket ? matchBucketLabels[variant.matchBucket] : 'Ведро сопоставления ещё не публикуется'}
              {variant.matchReason ? ` · ${labelFor(matchReasonLabels, variant.matchReason)}` : ''}
              {variant.matchUnavailableReason
                ? ` · ${labelFor(matchReadUnavailableReasonLabels, variant.matchUnavailableReason)}`
                : ''}
            </p>
          </SectionCard>

          <TechnicalIdentityLog variant={variant} />
        </LogsDisclosure>

        <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-rule pt-2 text-[11px]">
          <Link to="/analytics" className="text-ink-muted underline hover:text-ink">Аналитика по товарам</Link>
        </div>
      </div>
    </div>
  )
}
